import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Alert, IconButton, CircularProgress,
  Chip, TextField, MenuItem, Stack, ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider, Tooltip, LinearProgress
} from '@mui/material';
import { ArrowBack, Home, GridOn, Add, Print, AutoAwesome, RestartAlt, Publish, VisibilityOff } from '@mui/icons-material';
import axios from 'axios';
import TimetableNav from './TimetableNav';

const API = process.env.REACT_APP_API_URL;

const TimetableGrid = () => {
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [years, setYears] = useState([]);
  const [year, setYear]   = useState('');
  const [term, setTerm]   = useState(1);
  const [mode, setMode]   = useState('class');   // 'class' | 'cohort' | 'teacher'

  const [config, setConfig] = useState(null);
  const [classes, setClasses]   = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [stages, setStages]     = useState([]);
  const [allocations, setAllocations] = useState([]);

  const [target, setTarget]   = useState('');    // classCode | stageCode(cohort) | employeeNo
  const [entries, setEntries] = useState([]);
  const [loading, setLoading]       = useState(false);   // first load of a target — shows full spinner
  const [refreshing, setRefreshing] = useState(false);   // silent reload after place/remove — overlay only
  const [error, setError]     = useState('');
  const [dialogError, setDialogError] = useState('');    // errors shown inside the placement dialog
  const [placeAt, setPlaceAt] = useState(null);  // { day, periodNo }
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult]   = useState(null); // { placed:[], unplaced:[] }
  const [confirmScope, setConfirmScope] = useState(null); // 'all' | 'class' | null
  const [progress, setProgress] = useState(null); // { done, total, message }
  const [ttHeader, setTtHeader]   = useState(null);  // { status } for this year+term
  const [publishing, setPublishing] = useState(false);

  // Reference data
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/academics/years`, { headers }),
      axios.get(`${API}/api/academics/classes`, { headers }),
      axios.get(`${API}/api/employees`, { headers, params: { department: 'Teaching' } }),
      axios.get(`${API}/api/academics/learning-areas`, { headers }),
      axios.get(`${API}/api/stages`, { headers }),
      axios.get(`${API}/api/academics/current-period`, { headers }).catch(() => ({ data: {} })),
    ]).then(([y, c, t, s, st, cp]) => {
      setYears(y.data);
      // Default to the current period (active year + term in session).
      const active = y.data.find(yr => yr.isActive) || y.data[0];
      if (cp.data?.year) setYear(cp.data.year);
      else if (active)   setYear(active.year);
      if (cp.data?.term) setTerm(cp.data.term);
      setClasses(c.data); setTeachers(t.data); setSubjects(s.data); setStages(st.data);
    }).catch(err => setError(err.response?.data?.error || 'Failed to load reference data'));
  }, []); // eslint-disable-line

  const teacherName = useCallback((no) => {
    const t = teachers.find(x => x.employeeNo === no);
    return t ? `${t.firstName} ${t.lastName}` : no;
  }, [teachers]);
  const teacherInitials = useCallback((no) => {
    const t = teachers.find(x => x.employeeNo === no);
    if (!t) return no;
    return (`${(t.firstName || '')[0] || ''}${(t.lastName || '')[0] || ''}`).toUpperCase() || no;
  }, [teachers]);
  const subjectName = useCallback((code) => subjects.find(s => s.areaCode === code)?.areaName || code, [subjects]);

  // Config + allocations for the chosen year/term
  useEffect(() => {
    if (!year || !term) return;
    Promise.all([
      axios.get(`${API}/api/timetable/config`, { headers, params: { academicYear: year, term } }),
      axios.get(`${API}/api/timetable/allocations`, { headers, params: { academicYear: year, term } }),
      axios.get(`${API}/api/timetable/header`, { headers, params: { academicYear: year, term } }).catch(() => ({ data: null })),
    ]).then(([c, a, h]) => { setConfig(c.data); setAllocations(a.data); setTtHeader(h.data); })
      .catch(err => setError(err.response?.data?.error || 'Failed to load setup'));
  }, [year, term]); // eslint-disable-line

  // Publish makes the term's timetable visible to teachers (My Timetable / the
  // Class-Teacher Centre Timetable tab only ever show a PUBLISHED timetable).
  // Unpublish pulls it back to a draft while you keep editing.
  const isPublished = ttHeader?.status === 'Published';
  const togglePublish = async () => {
    setPublishing(true);
    setError('');
    try {
      const url = `${API}/api/timetable/${isPublished ? 'unpublish' : 'publish'}`;
      const { data } = await axios.post(url, { academicYear: year, term }, { headers });
      setTtHeader(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update publish status');
    } finally { setPublishing(false); }
  };

  // silent=true keeps the current table on screen (overlay progress) while refetching
  // after a place/remove — only the first load of a target shows the full spinner.
  const loadEntries = useCallback(async (silent = false) => {
    if (!year || !term || !target) { setEntries([]); return; }
    silent ? setRefreshing(true) : setLoading(true);
    if (!silent) setError('');
    try {
      const url = `${API}/api/timetable/entries`;
      if (mode === 'class') {
        // A class also needs its cohort's option blocks (reservation + the parallel
        // elective children have empty classCodes, so a classCode filter misses them).
        const stage = classes.find(c => c.classCode === target)?.stageCode;
        const reqs = [axios.get(url, { headers, params: { academicYear: year, term, classCode: target } })];
        if (stage) reqs.push(axios.get(url, { headers, params: { academicYear: year, term, cohortCode: stage } }));
        const results = await Promise.all(reqs);
        const map = {};
        results.forEach(r => (r.data || []).forEach(e => { map[e._id] = e; }));
        setEntries(Object.values(map));
      } else {
        const params = { academicYear: year, term };
        if (mode === 'cohort') params.cohortCode = target; else params.employeeNo = target;
        const { data } = await axios.get(url, { headers, params });
        setEntries(data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load timetable');
    } finally { silent ? setRefreshing(false) : setLoading(false); }
  }, [year, term, mode, target, classes]); // eslint-disable-line

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { setDialogError(''); }, [placeAt]);   // clear dialog error when the cell changes

  const days = config?.daysOfWeek || [];

  // The band that owns a stage's grid (falls back to a legacy default band).
  const bandForStage = useCallback((stageCode) => {
    const bands = config?.bands || [];
    const hit = bands.find(b => (b.appliesToStages || []).includes(stageCode));
    if (hit) return hit;
    if (bands.length === 1) return bands[0];
    if ((config?.periods || []).length) return { bandCode: 'DEFAULT', periods: config.periods };
    return null;
  }, [config]);

  const targetStage = useMemo(() => {
    if (mode === 'class')  return classes.find(c => c.classCode === target)?.stageCode;
    if (mode === 'cohort') return target;
    return null;
  }, [mode, target, classes]);

  // Columns: a class/cohort shows ITS band's periods; a teacher spans bands, so
  // show a merged real-time axis (union of all bands' lesson start times).
  const lessonPeriods = useMemo(() => {
    if (mode === 'teacher') {
      const all = (config?.bands || []).flatMap(b => (b.periods || []).filter(p => p.type === 'lesson'));
      if ((config?.periods || []).length) all.push(...config.periods.filter(p => p.type === 'lesson'));
      const byStart = {};
      all.forEach(p => { if (p.startTime && !byStart[p.startTime]) byStart[p.startTime] = { periodNo: p.startTime, startTime: p.startTime, endTime: p.endTime, lessonNo: null }; });
      return Object.values(byStart).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    }
    const band = bandForStage(targetStage);
    return (band?.periods || []).filter(p => p.type === 'lesson').sort((a, b) => a.periodNo - b.periodNo);
  }, [mode, config, targetStage, bandForStage]);

  // ── Class-view helpers (core lessons) ── teacher keys on real time (cross-band);
  // class matches only this class's own core lesson (not cohort elective children).
  const cellEntry = (day, p) => mode === 'teacher'
    ? entries.find(e => e.day === day && e.startTime === p.startTime && e.type !== 'optionBlock')
    : mode === 'class'
      ? entries.find(e => e.day === day && e.periodNo === p.periodNo && e.type === 'lesson' && (e.classCodes || []).includes(target))
      : entries.find(e => e.day === day && e.periodNo === p.periodNo && e.type !== 'optionBlock');
  const classAllocations = useMemo(
    () => (mode === 'class' && target)
      ? allocations.filter(a => a.kind === 'core' && (a.classCodes || []).includes(target))
      : [],
    [allocations, mode, target]
  );
  const placedCount = (a) => entries.filter(e => e.areaCode === a.areaCode && e.employeeNo === a.employeeNo).length;

  // ── Option-block helpers (shared by Cohort AND Class views) ──
  // Blocks belong to a cohort (stage); a class inherits its stage's blocks.
  const cohortBlocks = useMemo(() => {
    if (!targetStage) return [];
    const byBlock = {};
    allocations.filter(a => a.kind === 'elective' && a.cohortCode === targetStage)
      .forEach(a => { (byBlock[a.optionBlock || '—'] = byBlock[a.optionBlock || '—'] || []).push(a); });
    return Object.entries(byBlock).map(([block, allocs]) => ({ block, allocs }));
  }, [allocations, targetStage]);
  const fixedAt = (day, periodNo, bandCode) => (config?.fixedActivities || []).find(f =>
    f.day === day && f.periodNo === periodNo &&
    (!(f.bandCodes && f.bandCodes.length) || (bandCode && f.bandCodes.includes(bandCode))));
  const cellReservation = (day, periodNo) => entries.find(e => e.type === 'optionBlock' && e.day === day && e.periodNo === periodNo);
  // Only the parallel elective children (carry optionBlockId), never a class's core lesson.
  const cellChildren = (day, periodNo) => entries.filter(e => e.type === 'lesson' && e.optionBlockId && e.day === day && e.periodNo === periodNo);
  const blockLabel = (res) => (res.optionBlockId || '').replace(`${targetStage}-`, '');

  // ── Actions ──
  const place = async (alloc) => {
    setDialogError('');
    try {
      await axios.post(`${API}/api/timetable/entries`, {
        academicYear: year, term, day: placeAt.day, periodNo: placeAt.periodNo,
        type: 'lesson', employeeNo: alloc.employeeNo, areaCode: alloc.areaCode, classCodes: [target],
      }, { headers });
      setPlaceAt(null); loadEntries(true);
    } catch (err) { setDialogError(err.response?.data?.error || 'Could not place the lesson'); }
  };
  const placeBlock = async (block) => {
    setDialogError('');
    try {
      await axios.post(`${API}/api/timetable/entries/place-block`, {
        academicYear: year, term, day: placeAt.day, periodNo: placeAt.periodNo, cohortCode: targetStage, optionBlock: block,
      }, { headers });
      setPlaceAt(null); loadEntries(true);
    } catch (err) { setDialogError(err.response?.data?.error || 'Could not place the option block'); }
  };
  // Auto-populate from allocations (best-effort; honours all placement rules).
  // Clears the scope's existing lessons first so re-running never stacks duplicates.
  const runGenerate = async ({ scope, reset }) => {
    setConfirmScope(null); setError(''); setGenerating(true);
    setProgress({ done: 0, total: 0, message: 'Preparing…' });
    try {
      const body = { academicYear: year, term, reset };
      const url = scope === 'class'
        ? `${API}/api/timetable/generate/class`
        : `${API}/api/timetable/generate`;
      if (scope === 'class') body.classCode = target;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok || !resp.body) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(e.error || 'Auto-fill failed');
      }
      // Read the NDJSON progress stream line-by-line.
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '', final = null;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, nl).trim(); buf = buf.slice(nl + 1);
          if (!line) continue;
          let evt; try { evt = JSON.parse(line); } catch { continue; }
          if (evt.type === 'done')       final = evt;
          else if (evt.type === 'error') throw new Error(evt.error || 'Auto-fill failed');
          else if (evt.type === 'start') setProgress({ done: 0, total: evt.total || 0, message: evt.message || 'Working…' });
          else setProgress(p => ({ done: evt.done ?? p?.done ?? 0, total: evt.total ?? p?.total ?? 0, message: evt.message || p?.message }));
        }
      }
      if (final) setGenResult({ placed: final.placed, unplaced: final.unplaced });
      loadEntries(true);
    } catch (err) { setError(err.message || 'Auto-fill failed'); }
    finally { setGenerating(false); setProgress(null); }
  };

  const removeEntry = async (id) => {
    setError('');
    try { await axios.delete(`${API}/api/timetable/entries/${id}`, { headers }); loadEntries(true); }
    catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };
  const removeBlock = async (res) => {
    setError('');
    try {
      await axios.post(`${API}/api/timetable/entries/remove-block`, {
        academicYear: year, term, day: res.day, periodNo: res.periodNo, optionBlockId: res.optionBlockId,
      }, { headers });
      loadEntries(true);
    } catch (err) { setError(err.response?.data?.error || 'Could not remove the block'); }
  };

  const targetOptions =
    mode === 'class'  ? classes.map(c => ({ value: c.classCode, label: c.className || c.classCode })) :
    mode === 'cohort' ? stages.map(s => ({ value: s.code, label: `${s.name} (${s.code})` })) :
                        teachers.map(t => ({ value: t.employeeNo, label: `${t.firstName} ${t.lastName}` }));

  // ── Cell renderer ──
  const renderCell = (day, p) => {
    // Reserved slot (PPI, assembly…) for this target's band — shown, never schedulable.
    const fx = fixedAt(day, p.periodNo, bandForStage(targetStage)?.bandCode);
    if (fx) return (
      <Box component="td" key={p.periodNo} sx={{ bgcolor: '#eef3fb', textAlign: 'center' }}>
        <Tooltip title="Whole-school fixed activity — no lesson placed here">
          <Typography variant="caption" fontWeight={700} fontStyle="italic" display="block">{fx.label}</Typography>
        </Tooltip>
      </Box>
    );
    if (mode === 'cohort') {
      const res = cellReservation(day, p.periodNo);
      if (res) {
        const kids = cellChildren(day, p.periodNo);
        return (
          <Box component="td" key={p.periodNo} onClick={() => removeBlock(res)}
            sx={{ cursor: 'pointer', bgcolor: '#ede7f6', '&:hover': { bgcolor: '#ffcdd2' } }}>
            <Typography variant="caption" fontWeight={700} display="block">Block {blockLabel(res)}</Typography>
            <Tooltip title={kids.map(k => `${subjectName(k.areaCode)} · ${teacherName(k.employeeNo)}`).join(' | ') || 'no electives'}>
              <Typography variant="caption" color="text.secondary" display="block">{kids.length} electives</Typography>
            </Tooltip>
          </Box>
        );
      }
      return (
        <Box component="td" key={p.periodNo} onClick={() => setPlaceAt({ day, periodNo: p.periodNo })}
          sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f3e5f5' } }}>
          <Add fontSize="small" sx={{ color: 'action.disabled' }} />
        </Box>
      );
    }

    // class / teacher
    const e = cellEntry(day, p);
    if (e) {
      return (
        <Box component="td" key={p.periodNo}
          onClick={() => mode === 'class' && removeEntry(e._id)}
          sx={{ cursor: mode === 'class' ? 'pointer' : 'default', bgcolor: '#e3f2fd',
                '&:hover': mode === 'class' ? { bgcolor: '#ffcdd2' } : {} }}>
          <Tooltip title={subjectName(e.areaCode)}>
            <Typography variant="caption" fontWeight={700} display="block">{e.areaCode}</Typography>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" display="block">
            {mode === 'class'
              ? <Tooltip title={teacherName(e.employeeNo)}><span>{teacherInitials(e.employeeNo)}</span></Tooltip>
              : ((e.classCodes || []).length ? e.classCodes.join(', ') : `${e.cohortCode || ''} elective`)}
          </Typography>
        </Box>
      );
    }
    // class: an option block reserved for this class's cohort → parallel electives ("GEO / HIST")
    if (mode === 'class') {
      const res = cellReservation(day, p.periodNo);
      if (res) {
        const kids = cellChildren(day, p.periodNo);
        return (
          <Box component="td" key={p.periodNo} onClick={() => removeBlock(res)}
            sx={{ cursor: 'pointer', bgcolor: '#ede7f6', '&:hover': { bgcolor: '#ffcdd2' } }}>
            <Tooltip title={`Block ${blockLabel(res)} · ${kids.map(k => `${subjectName(k.areaCode)} (${teacherName(k.employeeNo)})`).join(', ')}`}>
              <Typography variant="caption" fontWeight={700} display="block">
                {kids.map(k => k.areaCode).join(' / ') || `Block ${blockLabel(res)}`}
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary" display="block">
              {kids.map(k => teacherInitials(k.employeeNo)).join(' / ') || `Block ${blockLabel(res)}`}
            </Typography>
          </Box>
        );
      }
    }
    return (
      <Box component="td" key={p.periodNo}
        onClick={() => mode === 'class' && setPlaceAt({ day, periodNo: p.periodNo })}
        sx={{ cursor: mode === 'class' ? 'pointer' : 'default', '&:hover': mode === 'class' ? { bgcolor: '#f1f8e9' } : {} }}>
        {mode === 'class' && <Add fontSize="small" sx={{ color: 'action.disabled' }} />}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: { xs: 1, sm: 0 } }}>
      <TimetableNav />
      <Card elevation={3}>
        <CardContent>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <GridOn color="primary" />
              <Box>
                <Typography variant="h5" color="primary">Timetable Grid</Typography>
                <Typography variant="body2" color="text.secondary">
                  Place lessons into the week — every change is checked against the golden rules
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {config && (
                <>
                  <Chip size="small" label={isPublished ? 'Published' : 'Draft'}
                    color={isPublished ? 'success' : 'default'} variant={isPublished ? 'filled' : 'outlined'} />
                  <Tooltip title={isPublished
                    ? 'Hide from teachers again (back to draft) while you keep editing'
                    : 'Make this term’s timetable visible to teachers (My Timetable & Class-Teacher Centre)'}>
                    <span>
                      <Button size="small" variant={isPublished ? 'outlined' : 'contained'}
                        color={isPublished ? 'inherit' : 'success'} disabled={publishing}
                        startIcon={publishing ? <CircularProgress size={14} /> : (isPublished ? <VisibilityOff /> : <Publish />)}
                        onClick={togglePublish} sx={{ whiteSpace: 'nowrap' }}>
                        {isPublished ? 'Unpublish' : 'Publish'}
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
              <Tooltip title="Print this view"><IconButton onClick={() => window.print()}><Print /></IconButton></Tooltip>
              <IconButton onClick={() => navigate('/academics')} title="Back to Academics"><ArrowBack /></IconButton>
              <IconButton onClick={() => navigate('/dashboard')} title="Home"><Home /></IconButton>
            </Box>
          </Box>

          {/* Controls */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
            <TextField select label="Academic Year" value={year} size="small" sx={{ minWidth: 150 }}
              onChange={e => setYear(e.target.value)}>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}{y.isActive ? ' (active)' : ''}</MenuItem>)}
            </TextField>
            <TextField select label="Term" value={term} size="small" sx={{ minWidth: 110 }}
              onChange={e => setTerm(parseInt(e.target.value, 10))}>
              {[1, 2, 3].map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
            </TextField>
            <ToggleButtonGroup size="small" exclusive value={mode}
              onChange={(_, v) => { if (v) { setMode(v); setTarget(''); setEntries([]); } }}>
              <ToggleButton value="class">By Class</ToggleButton>
              <ToggleButton value="cohort">By Cohort</ToggleButton>
              <ToggleButton value="teacher">By Teacher</ToggleButton>
            </ToggleButtonGroup>
            <TextField select label={mode === 'class' ? 'Class' : mode === 'cohort' ? 'Cohort' : 'Teacher'}
              value={target} size="small" sx={{ minWidth: 220 }} onChange={e => setTarget(e.target.value)}>
              {targetOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            {config && (
              <>
                <Tooltip title="Keep everything already placed (and manual tweaks) — only add what's missing from Teacher Allocations">
                  <Button variant="outlined" size="small" startIcon={generating ? <CircularProgress size={14} /> : <AutoAwesome />}
                    onClick={() => setConfirmScope({ scope: 'all', reset: false })} disabled={generating} sx={{ whiteSpace: 'nowrap' }}>
                    Fill gaps
                  </Button>
                </Tooltip>
                <Tooltip title="Clear the whole term and rebuild from Teacher Allocations (discards manual tweaks)">
                  <Button variant="outlined" color="warning" size="small" startIcon={<RestartAlt />}
                    onClick={() => setConfirmScope({ scope: 'all', reset: true })} disabled={generating} sx={{ whiteSpace: 'nowrap' }}>
                    Rebuild
                  </Button>
                </Tooltip>
              </>
            )}
            {mode === 'class' && target && (
              <>
                <Button variant="text" size="small" startIcon={<AutoAwesome />}
                  onClick={() => setConfirmScope({ scope: 'class', reset: false })} disabled={generating}>
                  Fill gaps · this class
                </Button>
                <Button variant="text" size="small" color="warning" startIcon={<RestartAlt />}
                  onClick={() => setConfirmScope({ scope: 'class', reset: true })} disabled={generating}>
                  Rebuild · this class
                </Button>
              </>
            )}
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {mode === 'teacher' && <Alert severity="info" sx={{ mb: 2 }}>Teacher view is read-only — place lessons from the class or cohort view.</Alert>}
          {mode === 'cohort' && <Alert severity="info" sx={{ mb: 2 }}>Placing a block reserves the whole cohort's slot and drops in every elective for that block at once.</Alert>}

          {!config ? (
            <Alert severity="warning">No timetable setup for {year || '—'} Term {term}. Seed it under Timetable Setup first.</Alert>
          ) : !target ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              Pick a {mode === 'class' ? 'class' : mode === 'cohort' ? 'cohort' : 'teacher'} to view its timetable.
            </Typography>
          ) : loading ? (
            <Box textAlign="center" py={4}><CircularProgress /></Box>
          ) : (
            <Box sx={{ overflowX: 'auto', position: 'relative' }}>
              {refreshing && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2, borderRadius: 1 }} />}
              <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 720,
                opacity: refreshing ? 0.55 : 1, transition: 'opacity .15s',
                '& td, & th': { border: '1px solid', borderColor: 'divider', p: 0.5, textAlign: 'center', verticalAlign: 'middle' } }}>
                <thead>
                  <tr>
                    <Box component="th" sx={{ bgcolor: 'grey.100', minWidth: 60 }}>Day</Box>
                    {lessonPeriods.map(p => (
                      <Box component="th" key={p.periodNo} sx={{ bgcolor: 'grey.100', minWidth: 90 }}>
                        <Typography variant="caption" fontWeight={700} display="block">
                          {p.lessonNo ? `P${p.lessonNo}` : (p.startTime || '')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.startTime}{p.endTime ? `–${p.endTime}` : ''}
                        </Typography>
                      </Box>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {days.map(day => (
                    <tr key={day}>
                      <Box component="td" sx={{ bgcolor: 'grey.50', fontWeight: 700 }}>{day}</Box>
                      {lessonPeriods.map(p => renderCell(day, p))}
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          )}

          {/* Class: lessons-to-place helper */}
          {mode === 'class' && target && classAllocations.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} mb={1}>Lessons to place (this class)</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {classAllocations.map(a => {
                  const placed = placedCount(a), left = a.lessonsPerWeek - placed;
                  return <Chip key={a._id} label={`${subjectName(a.areaCode)} · ${teacherName(a.employeeNo)} · ${placed}/${a.lessonsPerWeek}`}
                    color={left <= 0 ? 'success' : 'default'} variant={left <= 0 ? 'filled' : 'outlined'} size="small" />;
                })}
              </Box>
            </>
          )}

          {/* Cohort: available blocks helper */}
          {mode === 'cohort' && target && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} mb={1}>Option blocks for this cohort</Typography>
              {cohortBlocks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No elective allocations for this cohort. Add them under Teacher Allocations (kind = Elective).</Typography>
              ) : (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {cohortBlocks.map(b => (
                    <Chip key={b.block} label={`Block ${b.block} · ${b.allocs.length} subjects`} variant="outlined" size="small" color="secondary" />
                  ))}
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Placement dialog */}
      <Dialog open={Boolean(placeAt)} onClose={() => setPlaceAt(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {mode === 'cohort' ? 'Place an option block' : 'Place a lesson'} — {placeAt?.day} P{lessonPeriods.find(p => p.periodNo === placeAt?.periodNo)?.lessonNo ?? placeAt?.periodNo}
        </DialogTitle>
        <DialogContent dividers>
          {dialogError && <Alert severity="error" sx={{ mb: 1.5 }}>{dialogError}</Alert>}
          {mode === 'cohort' ? (
            cohortBlocks.length === 0 ? (
              <Typography color="text.secondary">No option blocks for this cohort yet.</Typography>
            ) : cohortBlocks.map(b => (
              <Box key={b.block} display="flex" alignItems="center" justifyContent="space-between" py={0.5}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>Block {b.block}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {b.allocs.map(a => subjectName(a.areaCode)).join(', ')}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => placeBlock(b.block)}>Place</Button>
              </Box>
            ))
          ) : (
            <>
              {classAllocations.length === 0
                ? <Typography color="text.secondary" sx={{ mb: 1 }}>No core allocations for this class.</Typography>
                : classAllocations.map(a => {
                    const placed = placedCount(a), left = a.lessonsPerWeek - placed;
                    return (
                      <Box key={a._id} display="flex" alignItems="center" justifyContent="space-between" py={0.5}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{subjectName(a.areaCode)}</Typography>
                          <Typography variant="caption" color="text.secondary">{teacherName(a.employeeNo)} · {placed}/{a.lessonsPerWeek} placed</Typography>
                        </Box>
                        <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => place(a)}
                          color={left <= 0 ? 'warning' : 'primary'}>{left <= 0 ? 'Place extra' : 'Place'}</Button>
                      </Box>
                    );
                  })}
              {cohortBlocks.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Option blocks (choose-one — run in parallel, e.g. GEO / HIST)
                  </Typography>
                  {cohortBlocks.map(b => (
                    <Box key={`blk-${b.block}`} display="flex" alignItems="center" justifyContent="space-between" py={0.5}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>Block {b.block}</Typography>
                        <Typography variant="caption" color="text.secondary">{b.allocs.map(a => subjectName(a.areaCode)).join(' / ')}</Typography>
                      </Box>
                      <Button size="small" variant="outlined" color="secondary" startIcon={<Add />} onClick={() => placeBlock(b.block)}>Place block</Button>
                    </Box>
                  ))}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setPlaceAt(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Live progress while generating */}
      <Dialog open={generating} maxWidth="xs" fullWidth>
        <DialogTitle>Generating timetable…</DialogTitle>
        <DialogContent dividers>
          <LinearProgress
            variant={progress?.total ? 'determinate' : 'indeterminate'}
            value={progress?.total ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : undefined}
            sx={{ height: 8, borderRadius: 4, mb: 1.5 }} />
          <Typography variant="body2" noWrap>{progress?.message || 'Preparing…'}</Typography>
          {progress?.total ? (
            <Typography variant="caption" color="text.secondary">
              {progress.done} / {progress.total} ({Math.round((progress.done / progress.total) * 100)}%)
            </Typography>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Generate confirmation — Fill gaps (keep) vs Rebuild (clear) */}
      <Dialog open={Boolean(confirmScope)} onClose={() => setConfirmScope(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmScope?.reset
            ? (confirmScope?.scope === 'class' ? 'Rebuild this class' : 'Rebuild timetable')
            : (confirmScope?.scope === 'class' ? 'Fill gaps — this class' : 'Fill gaps')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {confirmScope?.reset ? (
              <>This <strong>clears</strong> the {confirmScope?.scope === 'class' ? "class's" : "term's"} existing placed lessons and rebuilds them from
                Teacher Allocations — option blocks first, then core lessons — spread across the week, respecting clashes &amp; subject rules.
                Any manual tweaks will be lost.</>
            ) : (
              <>This <strong>keeps</strong> everything already placed (including manual tweaks) and only adds what's missing from
                Teacher Allocations — new subjects/classes and any shortfall — into free, valid slots. It does not remove lessons for
                allocations you've since deleted or changed (use Rebuild for that).</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmScope(null)}>Cancel</Button>
          <Button variant="contained" color={confirmScope?.reset ? 'warning' : 'primary'}
            startIcon={confirmScope?.reset ? <RestartAlt /> : <AutoAwesome />} onClick={() => runGenerate(confirmScope)}>
            {confirmScope?.reset ? 'Clear & rebuild' : 'Fill gaps'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auto-fill result */}
      <Dialog open={Boolean(genResult)} onClose={() => setGenResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Auto-fill result</DialogTitle>
        <DialogContent dividers>
          {genResult && (() => {
            const placedTotal = (genResult.placed || []).reduce((s, r) => s + (r.placed || 0), 0);
            const unplaced = genResult.unplaced || [];
            return (
              <>
                <Alert severity={unplaced.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
                  Placed {placedTotal} lesson{placedTotal !== 1 ? 's' : ''}{unplaced.length ? ` · ${unplaced.length} demand line${unplaced.length !== 1 ? 's' : ''} couldn't be fully placed` : ' — all demand placed'}.
                </Alert>
                {unplaced.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>Needs manual attention</Typography>
                    <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                      {unplaced.map((u, i) => {
                        const label = u.block
                          ? `${u.cohortCode} · Block ${u.block}`
                          : (u.areaCode
                              ? [teacherName(u.employeeNo), subjectName(u.areaCode), u.classCode || u.cohortCode].filter(Boolean).join(' · ')
                              : (u.cohortCode || 'Allocation'));
                        const counts = (u.placed != null && u.lessons != null) ? ` — ${u.placed}/${u.lessons}` : '';
                        return (
                          <Typography key={i} variant="body2" color="text.secondary" sx={{ py: 0.3 }}>
                            {label}{counts} · {u.reason}
                          </Typography>
                        );
                      })}
                    </Box>
                  </>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions><Button onClick={() => setGenResult(null)}>Done</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimetableGrid;
